import React, { useEffect, useState, useRef } from "react";

import { useRecordContext, useNotify } from "react-admin";

import { httpClient } from "./httpClient";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { bwColours } from "./theme";
import { CronChooser } from "./cronChooser";
import { HistoryChart } from "../charts/smartTank";
import {
  cToF,
  fToC,
  formatSetpoint,
  prefersFahrenheit,
  temperatureUnit,
} from "./temperature";
import Button from "@mui/material/Button";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

export const colours = {
  cooling: "#0B4F8A",
  heating: "#A13918",
  sparge: "#BF5C00",
};

const requestChangeSettings = async (
  record,
  key: string,
  value: number | string,
) => {
  return httpClient(
    "/api/v2/c/" +
      record.customerID +
      "/smart_tanks/" +
      record.id +
      "/settings",
    {
      method: "PATCH",
      body: JSON.stringify({ [key]: value }),
    },
  );
};

const useSettingsChanger = <T,>(
  record,
  key: string,
  label: string,
): [
  T,
  React.Dispatch<React.SetStateAction<T>>,
  React.Dispatch<React.SetStateAction<T>>,
  boolean,
] => {
  const notify = useNotify();
  const [value, setValue] = React.useState<T>(record.deviceSettings[key] as T);
  const [requesting, setRequesting] = React.useState<boolean>(false);

  // If the user clicks the refresh button in the topbar, then update the values
  useEffect(() => {
    setValue(record.deviceSettings[key] as T);
  }, [record, key]);

  const setAndCommitValue = (newValue: T) => {
    setValue(newValue);
    setRequesting(true);
    requestChangeSettings(record, key, newValue as number | string)
      .then(async (resp) => {
        if (resp.ok) {
          notify("Changed " + label, {
            type: "success",
          });
          record.deviceSettings[key] = newValue;
        } else {
          setValue(record.deviceSettings[key]);
          notify("Failed to change " + label, {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        setValue(record.deviceSettings[key]);
        notify("Failed to change " + label, {
          type: "error",
        });
        console.log(error);
      })
      .finally(() => {
        // FIXME: ideally we would refresh the whole record here, but this introduces
        // a race because backplane has not necessarily committed the database change yet
        //refresh();
        setRequesting(false);
      });
  };

  return [value, setValue, setAndCommitValue, requesting];
};

export const SmartTankTemperatureControls = ({ showControls }) => {
  const record = useRecordContext();

  // Temp Mode
  const [tempMode, , setAndCommitTempMode, tempModeRequesting] =
    useSettingsChanger<number>(record, "temp_mode", "temperature control mode");

  // Temp Setpoint
  const [
    tempSetpoint,
    setTempSetpoint,
    setAndCommitTempSetpoint,
    tempSetpointRequesting,
  ] = useSettingsChanger<number>(
    record,
    "temp_setpoint",
    "temperature setpoint",
  );
  // Display setpoints in the customer's preferred unit, applying the
  // controller firmware's snap-to-integer-F algorithm when using °F.
  //
  // The stored value is always in 10ths of °C, but the slider itself runs in
  // the display unit: 0.1 °C steps from -50 to 300 (i.e. -5…30 °C), or 1 °F
  // steps from 23 to 86 (the same range in °F).
  const useF = prefersFahrenheit();
  const toSliderValue = (tenths: number) =>
    tenths == null ? tenths : useF ? cToF(tenths / 10) : tenths;
  const fromSliderValue = (v: number) => (useF ? Math.round(fToC(v) * 10) : v);
  const tempSetpointFormat = (x) => {
    return formatSetpoint(useF ? fToC(x) : x / 10);
  };
  const tempSetpointMarks = [
    {
      value: toSliderValue(tempSetpoint),
      label: tempSetpointFormat(toSliderValue(tempSetpoint)),
    },
  ];

  if (!record) return null;

  const tempCtrlEnabled = record.deviceSettings.temp_ctl_enable >= 1;
  const temp_field =
    record.deviceSettings.temp_source === 4 ? "redox_temp" : "temp_1";

  return (
    // Stack the control panel below the chart on narrow screens.
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      <HistoryChart
        field={temp_field}
        actuationStates={["cooling_state", "heating_state"]}
        yLabel={`Temperature (${temperatureUnit()})`}
        // Stored-unit bounds; HistoryPlot converts them with convertValue.
        yMin={-5}
        yMax={30}
        convertValue={useF ? cToF : undefined}
        formatValue={(y) => {
          return `${y.toFixed(1)}${temperatureUnit()}`;
        }}
        colour={bwColours.accent.blue}
        style={{ height: 360, flexGrow: 1, minWidth: "50%" }}
      />
      <div style={{ width: 250, display: showControls ? "flex" : "none" }}>
        <FormControl
          focused={false}
          disabled={!tempCtrlEnabled}
          style={{ width: 100 }}
        >
          <FormLabel id="ctrltab-temperature-setpoint-label">
            Setpoint
          </FormLabel>
          <Slider
            aria-labelledby="ctrltab-temperature-setpoint-label"
            value={toSliderValue(tempSetpoint)}
            onChange={(_, value: number) => {
              setTempSetpoint(fromSliderValue(value));
            }}
            onChangeCommitted={(_, value: number) => {
              setAndCommitTempSetpoint(fromSliderValue(value));
            }}
            disabled={tempSetpointRequesting || !tempCtrlEnabled}
            min={useF ? cToF(-5) : -50}
            max={useF ? cToF(30) : 300}
            step={1}
            orientation="vertical"
            track={tempMode === 2 ? "inverted" : undefined}
            marks={tempCtrlEnabled ? tempSetpointMarks : []}
            valueLabelFormat={tempSetpointFormat}
            style={{ height: 300 }}
            sx={{
              color:
                tempMode === 1
                  ? colours.heating
                  : tempMode === 2
                    ? colours.cooling
                    : bwColours.primary.offblack,
              "& .MuiSlider-track": {
                border: "none",
                display: tempMode === 0 ? "none" : undefined,
              },
            }}
          />
        </FormControl>
        <FormControl
          focused={false}
          disabled={!tempCtrlEnabled}
          style={{ width: 150 }}
        >
          <FormLabel>Mode</FormLabel>
          <RadioGroup
            value={tempCtrlEnabled ? tempMode : 0}
            onChange={(e) => {
              setAndCommitTempMode(parseInt(e.target.value));
            }}
          >
            <FormControlLabel
              value={0}
              control={
                <Radio
                  sx={{
                    color: bwColours.primary.offblack,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: bwColours.primary.offblack,
                    },
                  }}
                />
              }
              label="Off"
              disabled={tempModeRequesting || !tempCtrlEnabled}
            />
            <FormControlLabel
              value={1}
              control={
                <Radio
                  sx={{
                    color: colours.heating,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: colours.heating,
                    },
                  }}
                />
              }
              label="Heating"
              disabled={tempModeRequesting || !tempCtrlEnabled}
            />
            <FormControlLabel
              value={2}
              control={
                <Radio
                  sx={{
                    color: colours.cooling,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: colours.cooling,
                    },
                  }}
                />
              }
              label="Cooling"
              disabled={tempModeRequesting || !tempCtrlEnabled}
            />
          </RadioGroup>
        </FormControl>
      </div>
    </div>
  );
};

export const SmartTankSpargeControls = ({ showControls }) => {
  const record = useRecordContext();

  // Sparge Mode
  const [spargeMode, , setAndCommitSpargeMode, spargeModeRequesting] =
    useSettingsChanger<number>(record, "sparge_mode", "sparge control mode");

  // Sparge Setpoint
  const [
    spargeSetpoint,
    setSpargeSetpoint,
    setAndCommitSpargeSetpoint,
    spargeSetpointRequesting,
  ] = useSettingsChanger<number>(
    record,
    "sparge_setpoint",
    "sparge redox setpoint",
  );
  const spargeSetpointFormat = (x) => {
    return x !== undefined ? x.toFixed(0) + " mV" : "";
  };
  const spargeSetpointMarks = [
    { value: spargeSetpoint, label: spargeSetpointFormat(spargeSetpoint) },
  ];

  if (!record) return null;

  const spargeEnabled =
    record.deviceSettings.redox_enable === 1 &&
    record.deviceSettings.sparge_enable === 1;

  return (
    // Stack the control panel below the chart on narrow screens.
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      <HistoryChart
        field="redox"
        actuationStates={["sparge_event"]}
        yLabel="Redox (mV)"
        yMin={-100}
        yMax={200}
        formatValue={(y) => {
          return `${y.toFixed(0)} mv`;
        }}
        colour={bwColours.accent.red}
        style={{ height: 360, flexGrow: 1, minWidth: "50%" }}
      />
      <div style={{ width: 250, display: showControls ? "flex" : "none" }}>
        <FormControl
          focused={false}
          disabled={!spargeEnabled}
          style={{ width: 100 }}
        >
          <FormLabel>Setpoint</FormLabel>
          <Slider
            value={spargeSetpoint}
            onChange={(_, value: number) => {
              setSpargeSetpoint(value);
            }}
            onChangeCommitted={(_, value: number) => {
              setAndCommitSpargeSetpoint(value);
            }}
            disabled={spargeSetpointRequesting || !spargeEnabled}
            min={-100}
            max={200}
            step={5}
            orientation="vertical"
            marks={spargeEnabled ? spargeSetpointMarks : undefined}
            valueLabelFormat={spargeSetpointFormat}
            style={{ height: 300 }}
            sx={{
              color:
                spargeMode === 1 ? colours.sparge : bwColours.primary.offblack,

              "& .MuiSlider-track": {
                border: "none",
                display: spargeMode === 0 ? "none" : undefined,
              },
            }}
          />
        </FormControl>
        <FormControl
          focused={false}
          disabled={!spargeEnabled}
          style={{ width: 150 }}
        >
          <FormLabel>Mode</FormLabel>
          <RadioGroup
            value={spargeEnabled ? spargeMode : 0}
            onChange={(e) => {
              setAndCommitSpargeMode(parseInt(e.target.value));
            }}
          >
            <FormControlLabel
              value={0}
              control={
                <Radio
                  sx={{
                    color: bwColours.primary.offblack,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: bwColours.primary.offblack,
                    },
                  }}
                />
              }
              label="Off"
              disabled={spargeModeRequesting || !spargeEnabled}
            />
            <FormControlLabel
              value={1}
              control={
                <Radio
                  sx={{
                    color: colours.sparge,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: colours.sparge,
                    },
                  }}
                />
              }
              label="Sparge"
              disabled={spargeModeRequesting || !spargeEnabled}
            />
          </RadioGroup>
        </FormControl>
      </div>
    </div>
  );
};

export const SmartTankPumpoverControls = ({ showControls }) => {
  const record = useRecordContext();

  // Pumpov Mode
  const [pumpovMode, , setAndCommitPumpovMode, pumpovModeRequesting] =
    useSettingsChanger(record, "pumpov_mode", "pumpover control mode");

  // Pumpover Cron
  const [pumpovCron, , setAndCommitPumpovCron, pumpovCronRequesting] =
    useSettingsChanger<string | null>(
      record,
      "pumpov_cron",
      "pumpover schedule",
    );

  const [
    pumpovDuration,
    ,
    setAndCommitPumpovDuration,
    pumpovDurationRequesting,
  ] = useSettingsChanger(record, "pumpov_duration", "pumpover duration");

  const [pumpovDurationFocused, setPumpovDurationFocused] = useState(false);
  const [pumpovDurationRaw, setPumpovDurationRaw] = useState("");
  const lastPumpovDurationValue = useRef<number | null>(null);

  useEffect(() => {
    if (
      lastPumpovDurationValue.current !== null &&
      lastPumpovDurationValue.current === pumpovDuration
    ) {
      lastPumpovDurationValue.current = null;
      return;
    }
    setPumpovDurationFocused(false);
    setPumpovDurationRaw("");
  }, [pumpovDuration]);

  const handlePumpovDurationFocus = () => {
    setPumpovDurationFocused(true);
    setPumpovDurationRaw(pumpovDuration != null ? String(pumpovDuration) : "");
  };

  const handlePumpovDurationBlur = () => {
    const parsed = parseInt(pumpovDurationRaw, 10);
    if (pumpovDurationRaw !== "" && !isNaN(parsed)) {
      setAndCommitPumpovDuration(parsed);
    }
    setPumpovDurationFocused(false);
    setPumpovDurationRaw("");
  };

  const handlePumpovDurationChange = (value: string) => {
    setPumpovDurationRaw(value);
  };

  if (!record) return null;

  const pumpovEnabled = record.deviceSettings.pumpov_enable === 1;

  return (
    // Stack the control panel below the chart on narrow screens.
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      <HistoryChart
        field="pumpover_state"
        dataSeries={[]}
        actuationStates={["pumpover_state"]}
        yLabel="Pumpover"
        yMin={0}
        yMax={1}
        formatValue={(y) => {
          return y > 0.5 ? "On" : "Off";
        }}
        colour={bwColours.primary.offblack}
        style={{ height: 360, flexGrow: 1, minWidth: "50%" }}
      />
      <div style={{ width: 250, display: showControls ? "block" : "none" }}>
        <FormControl focused={false} disabled={!pumpovEnabled}>
          <FormLabel>Control</FormLabel>
          <RadioGroup
            value={pumpovEnabled ? pumpovMode : 0}
            onChange={(e) => {
              setAndCommitPumpovMode(parseInt(e.target.value));
            }}
          >
            <FormControlLabel
              value={0}
              control={
                <Radio
                  sx={{
                    color: bwColours.primary.offblack,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: bwColours.primary.offblack,
                    },
                  }}
                />
              }
              label="Off"
              disabled={pumpovModeRequesting || !pumpovEnabled}
            />
            <FormControlLabel
              value={1}
              control={
                <Radio
                  sx={{
                    color: bwColours.accent.purple,
                    "&.Mui-checked:not(.Mui-disabled)": {
                      color: bwColours.accent.purple,
                    },
                  }}
                />
              }
              label="Scheduled"
              disabled={pumpovModeRequesting || !pumpovEnabled}
            />
          </RadioGroup>
        </FormControl>
        <FormControl focused={false} disabled={!pumpovEnabled}>
          <FormLabel>Schedule</FormLabel>
          <CronChooser
            value={pumpovCron}
            setValue={setAndCommitPumpovCron}
            disabled={pumpovCronRequesting || !pumpovEnabled}
          />
        </FormControl>
        <FormControl focused={false} disabled={!pumpovEnabled}>
          <FormLabel>Pumpover Duration</FormLabel>
          <TextField
            type={pumpovDurationFocused ? "number" : "text"}
            value={
              pumpovDurationFocused
                ? pumpovDurationRaw
                : pumpovDuration != null
                  ? pumpovDuration
                  : ""
            }
            onChange={(e) => handlePumpovDurationChange(e.target.value)}
            onFocus={handlePumpovDurationFocus}
            onBlur={handlePumpovDurationBlur}
            size="small"
            disabled={pumpovDurationRequesting || !pumpovEnabled}
            style={{ width: 150 }}
            sx={{
              "& .MuiInputBase-input": {
                paddingTop: "10px",
                paddingBottom: "10px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ fontWeight: 500 }}>
                  min
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </div>
    </div>
  );
};

export const ShowHideToggle = ({ value, setValue }) => {
  if (value) {
    return (
      <Button
        aria-label="toggle-controls"
        size="small"
        endIcon={<KeyboardDoubleArrowRightIcon />}
        onClick={() => {
          setValue(!value);
        }}
      >
        Hide Controls
      </Button>
    );
  }

  return (
    <Button
      aria-label="toggle-controls"
      size="small"
      startIcon={<KeyboardDoubleArrowLeftIcon />}
      onClick={() => {
        setValue(!value);
      }}
    >
      Show Controls
    </Button>
  );
};
